import { DraftOfferType } from '@/enterprise/data/useDraftOffer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { DraftOfferProperties, OfferBuilderFrequency, RateInformations } from '../../OfferBuilderContext';
import { type OfferSubmitStats } from '../../utils';
import { RENEWAL_TERM_DAYS } from '../PreferredAutoRenewal';
import { SubmitDraftOfferInput, SubmitDraftOfferRateInformations } from './useSubmitDraftOffer';

/**
 * Converts flat UI RateInformations to API discriminated union format
 */
function convertToSEARateInformations(
  rateInformations: RateInformations[],
  offerType: DraftOfferType
): SubmitDraftOfferRateInformations {
  return (rateInformations ?? []).map((ri) => {
    const { marketUuid, supplierDivisionUuid, rateInformation } = ri;

    // Build the discriminated union based on rate type
    if (rateInformation.rateType === 'REF') {
      return {
        marketUuid,
        supplierDivisionUuid,
        rateInformation: {
          rateType: 'REF' as const,
          spread: rateInformation.spread ?? 0,
        },
      };
    }

    // For APR or DISC, include rate
    return {
      marketUuid,
      supplierDivisionUuid,
      // rateInformation is calculated server-side by the platform API
      // for BENCHMARK offers, so we don't send it from the UI
      ...(offerType !== 'BENCHMARK' && {
        rateInformation: {
          rateType: rateInformation.rateType as 'APR' | 'DISC',
          rate: rateInformation.rate,
        },
      }),
    };
  });
}

export const buildDraftInput = ({
  selected,
  frequency,
  draftOfferProperties,
  autoRenewalChecked,
  submitStats,
}: {
  selected: TakerMarket[];
  frequency: OfferBuilderFrequency;
  draftOfferProperties: DraftOfferProperties;
  autoRenewalChecked: boolean;
  submitStats: OfferSubmitStats;
}): SubmitDraftOfferInput => {
  return {
    draftOfferUuid: draftOfferProperties.draftOfferUuid,
    draftOfferType: draftOfferProperties.offerType,
    draftType: draftOfferProperties.draftType,
    draftSource: draftOfferProperties.draftSource,
    frequency,
    rateInformations: convertToSEARateInformations(
      draftOfferProperties.rateInformations,
      draftOfferProperties.offerType
    ),
    ...(draftOfferProperties.offerType === 'PREFERRED' &&
      autoRenewalChecked && { renewalInformation: { enabled: true, termDays: RENEWAL_TERM_DAYS } }),
    ...(draftOfferProperties.termExpirationDate && {
      termExpirationDate: draftOfferProperties.termExpirationDate,
    }),
    takerMarkets: selected,
    ...submitStats,
    referrer: { workflow: 'OFFER_BUILDER' },
  };
};
